import type { ClimatePoint, StatPeriod } from '@/shared/data-types';

// 일사량을 슬롯 순서에 매핑 — day=시간(hour)키(**-1h 보정** `CLIMATE_HOUR_SHIFT`) · month/range=일자키 · year=월키. 구앱 daily positional(배열 index)
// 매칭 버그 교정: **일자/시간 키로 매칭**(결측일·다월 range 어긋남 방지). 키는 숫자만 추출해 하이픈/비하이픈 흡수.
//
// 발전량 조회(`StatSlot`)와 대시보드 추이(`TrendSlot`)가 **같은 규칙**을 써야 해서 shared 로 올렸다 — 위젯끼리는
// 동일 레이어라 직접 공유가 안 되고, 키 매칭을 각자 들면 두 화면의 일사량이 조용히 어긋난다. 슬롯 타입을 특정
// 위젯 것으로 묶지 않도록 **구조적 최소 형태**(`{date, time}`)만 받는다.

const digits = (value: string | null): string => (value ?? '').replace(/\D/g, '');

/** 일사량 매칭에 필요한 슬롯 최소 형태 — `StatSlot`·`TrendSlot` 둘 다 구조적으로 만족한다. */
export interface IrradianceSlot {
  /** yyyy-MM-dd. */
  date: string;
  /** HH:mm:ss. */
  time: string;
}

/**
 * 슬롯 → 기상 매칭 키(period 별). 일사량(climate)·발전소 기상(detail/ranged)이 **같은 규칙**을 써야 한 표 안에서
 * 두 소스가 어긋나지 않으므로 export 한다.
 */
export function slotWeatherKey(period: StatPeriod, slot: IrradianceSlot): string {
  if (period === 'day') return digits(slot.time).slice(0, 2); // hour
  if (period === 'year') return digits(slot.date).slice(4, 6); // month
  return digits(slot.date).slice(0, 8); // yyyymmdd (month/range)
}

/**
 * 정시 관측 → 구간 몫 보정(**day 한정**, 시간 단위). 기상 시간자료의 `HH시` 값은 **그 정시까지의 직전 1시간
 * 누적**(06시 자료 = 05:00~06:00)인 반면, 발전량 슬롯 `HH` 는 `[HH, HH+1)` 구간의 몫이다(표·툴팁 라벨이
 * 'HH시 ~ HH+1시' — `GEN_HOUR_LABEL`). 그래서 관측 시각을 그대로 슬롯 키로 쓰면 일사량 곡선이 발전량보다
 * **한 시간 오른쪽으로** 밀려 그려진다 — 한 칸 당겨(-1h) 같은 구간끼리 맞춘다.
 *
 * 일(day) 이상 단위(month/year/range)엔 이 규약이 없어 보정하지 않는다.
 *
 * 결과표의 기상 열(발전소 기상 `detail/ranged`)도 **같은 규약**으로 보정한다 — 다만 그쪽은 슬롯에서 응답을
 * 찾아가는 방향이라 부호가 반대이고(`nextHourKey`, +1h), 날씨·기온·습도까지 행 단위로 함께 당긴다
 * (`mapPlantWeatherToSlots` 참조).
 *
 * ⚠️ 두 소스(좌표 기반 기상청 지점 vs 발전소 수집)의 시간 규약은 아직 미확인이다.
 * [TODO:be-verify] 실 응답으로 확정 — 구간 몫으로 오는 것이면 양쪽 보정을 함께 걷어낸다.
 */
const CLIMATE_HOUR_SHIFT = -1;

/** 기상 point → 일사량 매칭 키(period 별). 키 산출 불가면 null(스킵). */
function climateIrradianceKey(period: StatPeriod, point: ClimatePoint): string | null {
  if (period === 'day') {
    const hh = digits(point.time).slice(0, 2);
    if (hh.length < 2) return null;
    const hour = Number(hh) + CLIMATE_HOUR_SHIFT;
    // 00시 자료는 보정하면 전날 23시 몫이 되어 이 날 슬롯에 자리가 없다 — 버린다(day 슬롯은 5~20시라 무해).
    if (!Number.isFinite(hour) || hour < 0) return null;
    return String(hour).padStart(2, '0');
  }
  if (period === 'year') return digits(point.date).slice(4, 6) || null;
  const ymd = digits(point.date).slice(0, 8);
  return ymd.length === 8 ? ymd : null;
}

/**
 * 슬롯 순서에 맞춘 일사량 배열. **단일 발전소 + 좌표** 있을 때만 의미(전체는 좌표가 하나로 안 정해진다).
 *
 * **매칭 포인트가 없는 슬롯은 `null`** — 0 으로 눕히면 "그 시각 일사량이 0 이었다"는 사실 아닌 값이 되고,
 * 영역/라인이 바닥을 훑으며 이어져 결측 구간이 보이지 않는다(recharts `connectNulls=false` 라 null 이면 끊긴다).
 * 값이 실제로 0 인 야간은 BE 가 0 을 주므로 그대로 0 으로 남아 구분된다. 발전량(`aggregateTrendByPlant`)·
 * 표 기상(`mapPlantWeatherToSlots`)과 같은 규칙.
 */
export function mapIrradianceToSlots(
  climate: ClimatePoint[],
  slots: IrradianceSlot[],
  period: StatPeriod,
): Array<number | null> {
  const byKey = new Map<string, number>();
  for (const point of climate) {
    if (point.irradiance == null) continue;
    const key = climateIrradianceKey(period, point);
    if (key) byKey.set(key, point.irradiance);
  }
  return slots.map((slot) => byKey.get(slotWeatherKey(period, slot)) ?? null);
}
